import React, { useEffect, useRef, useState } from 'react';

// Keep cards static while using the video's first frame when no cover exists.
export default function VideoPosterPreview({ poster, videoUrl, alt, onLoad, onError }) {
    const [failedPoster, setFailedPoster] = useState(null);
    const videoRef = useRef(null);
    const [activatedUrl, setActivatedUrl] = useState(null);
    const active = Boolean(videoUrl) && activatedUrl === videoUrl;
    useEffect(() => {
        const video = videoRef.current;
        if (!video || active) return undefined;
        if (typeof IntersectionObserver !== 'function') {
            setActivatedUrl(videoUrl);
            return undefined;
        }
        let disposed = false;
        const observer = new IntersectionObserver(entries => {
            if (disposed) return;
            if (entries.some(entry => entry.isIntersecting)) {
                setActivatedUrl(videoUrl);
                observer.disconnect();
            }
        }, { rootMargin: '200px' });
        observer.observe(video);
        return () => {
            disposed = true;
            observer.disconnect();
        };
    }, [videoUrl, poster, failedPoster, active]);
    const style = {
        borderRadius: 8,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block'
    };

    if (poster && failedPoster !== poster) {
        return <img
            src={poster}
            alt={alt}
            referrerPolicy="no-referrer"
            onLoad={onLoad}
            onError={event => {
                setFailedPoster(poster);
                onError?.(event.target.currentSrc || event.target.src);
            }}
            style={style}
        />;
    }

    if (!videoUrl) return null;

    return <video
        key={videoUrl}
        ref={videoRef}
        src={active ? videoUrl : undefined}
        preload={active ? 'metadata' : 'none'}
        muted
        playsInline
        aria-hidden="true"
        referrerPolicy="no-referrer"
        onLoadedMetadata={event => {
            const video = event.currentTarget;
            if (Number.isFinite(video.duration) && video.duration > 0) {
                video.currentTime = Math.min(0.1, video.duration / 2);
            }
        }}
        onLoadedData={onLoad}
        onError={event => onError?.(event.currentTarget.currentSrc || videoUrl)}
        style={{ ...style, pointerEvents: 'none' }}
    />;
}
